import ConsentCopiesList from "../../../../../shared/ui/consent/ConsentCopiesList";
import { useTranslation } from "react-i18next";
import { Send, Download, FileWarning } from "lucide-react";
import Button from "../../../../../shared/ui/button/Button";
import { usePatientCompliance } from "../../../application/usePatientCompliance";
import PatientExportDataCard from "./PatientExportDataCard";
import ComponentCard from "../../../../../shared/ui/common/ComponentCard";
import type { Patient } from "../../../domain/Patient";
import type { PatientInvitation } from "../../../domain/PatientInvitation";

interface PatientComplianceTabProps {
  patient: Patient;
  onExport: () => void;
}

export default function PatientComplianceTab({ patient, onExport }: PatientComplianceTabProps) {
  const { t } = useTranslation();

  const {
    latestInvitation,
    sendingInvitation,
    invitationFeedback,
    invitationLanguage,
    setInvitationLanguage,
    sendInvitation: handleSendInvitation,
    consentStatus,
    consentActionLoading,
    showRevokeConfirm,
    setShowRevokeConfirm,
    recordConsent: handleRecordConsent,
    revokeConsent: handleRevokeConsent,
    consentCopies,
    downloadingCopyId,
    downloadConsentCopy: handleDownloadConsentCopy,
    consentDocumentUrl,
    consentDocumentFailed,
  } = usePatientCompliance(patient);

  const invitationStateBadge = (state: PatientInvitation["state"]) => {
    const classes: Record<PatientInvitation["state"], string> = {
      pending: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
      accepted: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
      expired: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
      revoked: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400",
    };
    const labels: Record<PatientInvitation["state"], string> = {
      pending: t("patient.portalInvitation.statePending"),
      accepted: t("patient.portalInvitation.stateAccepted"),
      expired: t("patient.portalInvitation.stateExpired"),
      revoked: t("patient.portalInvitation.stateRevoked"),
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${classes[state]}`}>
        {labels[state]}
      </span>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Portal invitation */}
      <ComponentCard title={t("patient.portalInvitation.label")}>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2">
            {latestInvitation ? invitationStateBadge(latestInvitation.state) : (
              <span className="text-sm text-content-tertiary">{t("patient.portalInvitation.noInvitation")}</span>
            )}
            <div className="flex items-center gap-2">
              {latestInvitation?.state !== "accepted" && (
                <select
                  value={invitationLanguage}
                  onChange={(e) => setInvitationLanguage(e.target.value as "es" | "en")}
                  className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 text-content-secondary focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="es">🇪🇸 ES</option>
                  <option value="en">🇬🇧 EN</option>
                </select>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={handleSendInvitation}
                disabled={latestInvitation?.state === "accepted"}
                loading={sendingInvitation}
                startIcon={<Send className="w-3.5 h-3.5" />}
                className="text-brand-500 hover:bg-brand-50 border-brand-200"
              >
                {sendingInvitation
                  ? t("patient.portalInvitation.sending")
                  : latestInvitation
                    ? t("patient.portalInvitation.resendInvitation")
                    : t("patient.portalInvitation.sendInvitation")}
              </Button>
            </div>
          </div>
          {invitationFeedback === "sent" && (
            <span className="text-xs text-green-600">{t("patient.portalInvitation.sent")}</span>
          )}
          {invitationFeedback === "error" && (
            <span className="text-xs text-red-500">{t("patient.portalInvitation.error")}</span>
          )}
        </div>
      </ComponentCard>

      {/* Consent */}
      <ComponentCard
        title={
          <div className="flex items-center justify-between w-full">
            <span>{t("patient.consent.label")}</span>
            {consentStatus?.is_active && !showRevokeConfirm && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowRevokeConfirm(true)}
                className="text-red-500 border-red-200 hover:bg-red-50 font-normal"
              >
                {t("patient.consent.revokeButton")}
              </Button>
            )}
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${consentStatus?.is_active ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]' : 'bg-gray-300'}`} />
              <span className="text-sm font-medium text-content-secondary">
                {consentStatus?.is_active ? t("patient.consent.active") : t("patient.consent.inactive")}
              </span>
              {consentStatus?.is_active && consentStatus.accepted_at && (
                <span className="text-xs text-content-tertiary ml-1">
                  · {new Date(consentStatus.accepted_at).toLocaleDateString()}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {consentDocumentUrl && (
                <a
                  href={consentDocumentUrl}
                  download="consentimiento_informado.pdf"
                  className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 font-medium"
                >
                  <Download className="w-3.5 h-3.5" />
                  {t("patient.consent.download")}
                </a>
              )}
            </div>
          </div>
          <ConsentCopiesList
            copies={consentCopies}
            onDownload={handleDownloadConsentCopy}
            downloadingId={downloadingCopyId}
          />
          {consentDocumentFailed && (
            <div className="flex items-start gap-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 px-3 py-2.5">
              <FileWarning className="w-4 h-4 text-content-tertiary shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-content-secondary">
                  {t("patient.consent.documentUnavailableTitle")}
                </p>
                <p className="text-xs text-content-tertiary mt-0.5">
                  {t("patient.consent.documentUnavailableDesc")}
                </p>
              </div>
            </div>
          )}
          {!consentStatus?.is_active && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleRecordConsent}
              loading={consentActionLoading}
              className="self-start text-brand-600 border-brand-200 hover:bg-brand-50"
            >
              {consentActionLoading
                ? t("patient.consent.recording")
                : t("patient.consent.recordButton")}
            </Button>
          )}
          {showRevokeConfirm && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-red-600">{t("patient.consent.revokeConfirm")}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={handleRevokeConsent}
                loading={consentActionLoading}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                {t("common.yes")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowRevokeConfirm(false)}
              >
                {t("common.cancel")}
              </Button>
            </div>
          )}
        </div>
      </ComponentCard>

      {/* Export */}
      <PatientExportDataCard onExport={onExport} />
    </div>
  );
}
